import { CartDto } from '@cineamo/api/models/cart/CartDto.types';
import { CartConcessionDto } from '@cineamo/api/models/cart-concession/CartConcessionDto.types';
import { CinemaProductDto } from '@cineamo/api/models/cinema-product/CinemaProductDto.types';

import { ApiError } from '@cineamo/api/types/ApiErrorResponse.types';

import { ReasonCode } from '@cineamo/api/constants/error-code-constants';

/** Rate the backend assumes for a product without one (`ProductPriceCalculator`). */
const DEFAULT_TAX = 19;

/**
 * Gross cents per unit including the online ticketing fee. Mirrors `ProductPriceCalculator`: strip the tax,
 * add the fee on the net, re-apply the tax, rounding per step. The unit result is multiplied by the quantity
 * afterwards, so the lines add up to the cart's `priceTotal`.
 */
export function withOnlineTicketingFee(
    grossCents: number,
    tax: CinemaProductDto['tax'],
    feePercent: NonNullable<CartDto['onlineTicketingFee']>
): number {
    const rate = 1 + (tax ?? DEFAULT_TAX) / 100;
    const net = Math.round(grossCents / rate);
    return Math.round(Math.round(net * (1 + feePercent / 100)) * rate);
}

export function concessionLineTotal(
    concession: Pick<CartConcessionDto, 'unitPrice' | 'tax' | 'quantity'>,
    feePercent: NonNullable<CartDto['onlineTicketingFee']>
): number {
    return withOnlineTicketingFee(concession.unitPrice, concession.tax, feePercent) * concession.quantity;
}

/** Translation key (below `tickets.error`) for a failed `PUT /carts/{uuid}/concessions`. */
export function concessionErrorKey(error: ApiError): string {
    if (error.status === 403) {
        return error.data?.reason_code === ReasonCode.CART_IS_PAID
            ? 'concession-cart-paid'
            : 'concession-sale-disabled';
    }
    const productErrors = (error.data?.parameters as Record<string, Record<string, string>> | undefined)
        ?.cinemaProductId;
    if (productErrors?.isInactive) return 'concession-product-inactive';
    if (productErrors?.notSameCinema) return 'concession-product-other-cinema';
    if (productErrors?.isUnknown) return 'concession-product-unknown';
    return 'something-went-wrong';
}
